import { autoinject, transient, computedFrom, useView } from "aurelia-framework";
import { ChildComponent } from "../../../child-component";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { RightShipInspectionClientModel, RightShipInspectionDeficiencyClientModel, RightShipInspectionStatusEnum, type RightShipInspectionStatusClientModel } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { RightShipInspectionsService } from "../../../../../../services/rightship-inspections/rightship-inspections.service";
import { UnitOfWorkItem } from "../../../../../../utilities/unit-of-work-observer";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { InspectionDeficiencyOutcomeStepEnum } from "../../../../../../models/vetting/vetting.models";
import { Assignment } from "../../../../../components/assignment/assignment";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";

@autoinject
@transient()
@useView('views/pages/modules/rightship-inspection/steps/deficiencies/rightship-inspection-deficiencies.html')
export class RightShipInspectionDeficiencies extends ChildComponent<RightShipInspectionClientModel>
{
    public getChanges: (() => { [index: string]: UnitOfWorkItem });
    public clearChanges: () => void;
    public refreshHistoryTrail: () => void;
    public pass: number = InspectionDeficiencyOutcomeStepEnum.Pass;
    public fail: number = InspectionDeficiencyOutcomeStepEnum.Fail;
    public assignment: Assignment;
    public deficienciesSnapshot?: RightShipInspectionDeficiencyClientModel[];

    activate(params: any) {
        this.assignment = params.assignment;
        return super.activate(params.item);
    }

    @computedFrom("item.statuses.length")
    public get currentStatus(): RightShipInspectionStatusClientModel {
        if (this.item && this.item.statuses && this.item.statuses.length > 0) {
            return this.item.statuses[0];
        } else return null;
    }

    @computedFrom("currentStatus.statusId")
    public get isCanceled(): boolean {
        if (!this.currentStatus || !this.currentStatus.statusId) return true;

        return this.currentStatus.statusId == RightShipInspectionStatusEnum.Cancelled;
    }

    @computedFrom("currentStatus.statusId")
    public get isClosed(): boolean {
        if (!this.currentStatus || !this.currentStatus.statusId) return true;

        return this.currentStatus.statusId == RightShipInspectionStatusEnum.Acceptable || this.currentStatus.statusId == RightShipInspectionStatusEnum.Unacceptable;
    }

    constructor(notificationsService: NotificationClientService, i18N: I18N, private rightShipInspectionService: RightShipInspectionsService,
        private userClientService: UserClientService, private dialogService: DialogClientService) {
        super(notificationsService, i18N);
    }

    public get inspectionType() {
        if (this.item) {
            switch (this.item.inspectionTypeId) {
                case 1: return "RightShip";
                case 2: return "Cargill";
                case 3: return "Tanker";
            }
        }

        return "";
    }

    radioButtonChanged(def: RightShipInspectionDeficiencyClientModel, prop: string, value: boolean, propValue?: boolean) {
        if (value) {
            if (def[prop] == propValue) return;

            const oldValue = def[prop];
            def[prop] = propValue;

            if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo != this.userClientService.currentUser.id) {
                this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                    name: `${this.inspectionType} Inspection`,
                    submit: async () => {
                        if (this.assignment) {
                            await this.assignment.assignToMe();
                        }
                    },
                    onCancel: () => {
                        def[prop] = oldValue;
                    },
                    currentAssignment: this.assignment.assignedEntity.assignedToUserName
                }, true, false, false);
            }
        }
    }

    save = async () => {
        if (!this.item) {
            return;
        }

        const changes = this.getChanges();

        this.renameProperty(changes, "deficiencies", "rightShipInspectionDeficiencies");

        const deficiencies = changes["rightShipInspectionDeficiencies"];

        if (deficiencies) {
            for (const id in deficiencies) {
                const item = deficiencies[id];
                this.renameProperty(item, "likelyToOccurInVesselBool", "likelyToOccurInVesselAgain");
                this.renameProperty(item, "likelyToOccurWithinFleetBool", "likelyToOccurWithinFleet");
            }
        }

        this.clearChanges();

        try {
            await this.rightShipInspectionService.saveRightShipInspection({
                sendEmail: false,
                id: this.item.rightShipInspectionId,
                changes: changes,
                skipValidation: true
            });

            this.notificationService.show("Deficiencies saved successfully", "", NotificationType.Success);

            this.refreshHistoryTrail();
        } catch{
            this.notificationService.show("Deficiencies saved successfully", "", NotificationType.Error);
        }
    }

    reasonChanged(def: RightShipInspectionDeficiencyClientModel, value: string, oldValue: string) {
        if (def.reason == value) return;

        def.reason = value;

        if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo != this.userClientService.currentUser.id) {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                name: `${this.inspectionType} Inspection`,
                submit: async () => {
                    if (this.assignment) {
                        await this.assignment.assignToMe();
                    }
                },
                onCancel: () => {
                    def.reason = oldValue;
                },
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    deficiencyIdChanged(def: RightShipInspectionDeficiencyClientModel, value: number, oldValue: number) {
        if (def.deficiencyId == value) return;

        def.deficiencyId = value;

        if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo != this.userClientService.currentUser.id) {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                name: `${this.inspectionType} Inspection`,
                submit: async () => {
                    if (this.assignment) {
                        await this.assignment.assignToMe();
                    }
                },
                onCancel: () => {
                    def.deficiencyId = oldValue;
                },
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    deficiencyRiskAreaIdChanged(def: RightShipInspectionDeficiencyClientModel, value: number, oldValue: number) {
        if (def.deficiencyRiskAreaId == value) {
            return;
        }

        def.deficiencyRiskAreaId = value;

        if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo != this.userClientService.currentUser.id) {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                name: `${this.inspectionType} Inspection`,
                submit: async () => {
                    if (this.assignment) {
                        await this.assignment.assignToMe();
                    }
                },
                onCancel: () => {
                    def.deficiencyRiskAreaId = oldValue;
                },
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    deficiencyCategoryIdChanged(def: RightShipInspectionDeficiencyClientModel, value: number, oldValue: number) {
        if (def.deficiencyCategoryId == value) {
            return;
        }

        def.deficiencyCategoryId = value;

        if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo != this.userClientService.currentUser.id) {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                name: `${this.inspectionType} Inspection`,
                submit: async () => {
                    if (this.assignment) {
                        await this.assignment.assignToMe();
                    }
                },
                onCancel: () => {
                    def.deficiencyCategoryId = oldValue;
                },
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    setOutcome(def: RightShipInspectionDeficiencyClientModel, outcome: number) {
        if (this.assignment && this.assignment.assignedEntity && this.assignment.assignedEntity.assignedTo == this.userClientService.currentUser.id) {
            if (outcome == this.pass && def.reason) {
                def.reason = "";
            }
            def.vetSuperOutcomeLastModifiedDate = new Date();
            def.vetSuperOutcomeModifiedBy = this.userClientService.currentUser.id;
            def.vetSuperOutcomeId = outcome;
            def.vetSuperModifiedOutcome = true;
        } else {
            this.dialogService.show(RightShipDialogServiceConstants.URLs.VetAssignmentDialog, {
                name: `${this.inspectionType} Inspection`,
                submit: async () => {
                    if (this.assignment) {
                        await this.assignment.assignToMe();
                    }
                },
                callback: () => this.setOutcome(def, outcome),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    renameProperty(obj: any, oldName: string, newName: string) {
        // Do nothing if the names are the same
        if (oldName === newName) {
            return obj;
        }
        // Check for the old property name to avoid a ReferenceError in strict mode.
        if (Object.prototype.hasOwnProperty.call(obj, oldName)) {
            obj[newName] = obj[oldName];
            delete obj[oldName];
        }
        return obj;
    }

    onActivated() {
        this.deficienciesSnapshot = CloneHelper.cloneObject(this.item.deficiencies);
    }

    discardChanges() {
        this.deficienciesSnapshot.forEach(c => {
            const existingDef = this.item.deficiencies.filter(x => x.rightShipInspectionDeficiencyId == c.rightShipInspectionDeficiencyId)[0];
            if (existingDef) {
                existingDef.deficiencyRiskAreaId = c.deficiencyRiskAreaId;
                existingDef.deficiencyCategoryId = c.deficiencyCategoryId;
                existingDef.deficiencyId = c.deficiencyId;
                existingDef.deficiency = c.deficiency;
                existingDef.code = c.code;
                existingDef.group = c.group;
                existingDef.likelyToOccurInVessel = c.likelyToOccurInVessel;
                existingDef.likelyToOccurInFleet = c.likelyToOccurInFleet;
                existingDef.comment = c.comment;
                existingDef.reason = c.reason;
                existingDef.createdDate = c.createdDate;
                existingDef.lastModifiedDate = c.lastModifiedDate;
                existingDef.vetSuperOutcomeLastModifiedDate = c.vetSuperOutcomeLastModifiedDate;
                existingDef.vetSuperOutcomeModifiedBy = c.vetSuperOutcomeModifiedBy;
                existingDef.vetSuperOutcomeId = c.vetSuperOutcomeId;
                existingDef.vetSuperModifiedOutcome = c.vetSuperModifiedOutcome;
                existingDef.manualDefectText = c.manualDefectText;
            }
        })
        this.deficienciesSnapshot = null;
        this.clearChanges();
    }

    hasChanges() {
        const changes = this.getChanges();

        this.renameProperty(changes, "deficiencies", "rightShipInspectionDeficiencies");

        const deficiencies = changes["rightShipInspectionDeficiencies"];
        if (deficiencies) {
            return true;
        }
        return false;
    }
}